import React, { useState } from "react";
import { CheckIcon, XIcon } from "lucide-react";
import { Button } from "../ui/button";
import { Avatar, AvatarFallback } from "../ui/avatar";
import {
  Friend,
  acceptFriendRequest,
  rejectFriendRequest,
} from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";
import toast from "react-hot-toast";

interface FriendRequestItemProps {
  request: Friend;
  onRequestHandled?: (requestId: string) => void;
  onFriendAccepted?: () => void;
}

export const FriendRequestItem: React.FC<FriendRequestItemProps> = ({
  request,
  onRequestHandled,
  onFriendAccepted,
}) => {
  const { user } = useAuth();
  const [isAccepting, setIsAccepting] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);

  // Get avatar URL with fallback
  const getAvatarUrl = () => {
    if (request.user_profiles?.avatar_url) {
      return request.user_profiles.avatar_url;
    }
    return "/avatars/ava1.png";
  };

  const handleAccept = async () => {
    if (!user?.id || isAccepting || isRejecting) return;

    setIsAccepting(true);

    try {
      const result = await acceptFriendRequest(
        request.id,
        user.id,
        request.user_id
      );

      if (result.success) {
        toast.success(
          `Permintaan pertemanan dari ${request.user_profiles?.name} diterima`
        );

        if (onRequestHandled) {
          onRequestHandled(request.id);
        }

        // Trigger friend list refresh
        if (onFriendAccepted) {
          onFriendAccepted();
        }
      } else {
        toast.error(result.error || "Gagal menerima permintaan pertemanan");
      }
    } catch (error) {
      console.error("Error accepting friend request:", error);
      toast.error("Terjadi kesalahan saat menerima permintaan pertemanan");
    } finally {
      setIsAccepting(false);
    }
  };

  const handleReject = async () => {
    if (!user?.id || isAccepting || isRejecting) return;

    setIsRejecting(true);

    try {
      const result = await rejectFriendRequest(request.id);

      if (result.success) {
        toast.success(
          `Permintaan pertemanan dari ${request.user_profiles?.name} ditolak`
        );

        if (onRequestHandled) {
          onRequestHandled(request.id);
        }
      } else {
        toast.error(result.error || "Gagal menolak permintaan pertemanan");
      }
    } catch (error) {
      console.error("Error rejecting friend request:", error);
      toast.error("Terjadi kesalahan saat menolak permintaan pertemanan");
    } finally {
      setIsRejecting(false);
    }
  };

  return (
    <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
      {/* Avatar */}
      <Avatar className="w-8 h-8 flex-shrink-0">
        <AvatarFallback>
          <img
            className="w-full h-full object-cover"
            alt="User Avatar"
            src={getAvatarUrl()}
          />
        </AvatarFallback>
      </Avatar>

      {/* User Info */}
      <div className="flex-1 min-w-0">
        <h4 className="font-medium text-[#373e46] text-sm truncate">
          {request.user_profiles?.name || "Unknown User"}
        </h4>
        <p className="text-xs text-gray-500 truncate">
          {request.user_profiles?.asal_sekolah}
        </p>
        <p className="text-xs text-gray-500 truncate">
          {request.user_profiles?.role}
        </p>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-1 flex-shrink-0">
        <Button
          onClick={handleAccept}
          disabled={isAccepting || isRejecting}
          size="sm"
          className={`px-2 py-1 rounded-lg font-medium transition-all duration-300 text-xs h-7 flex items-center gap-1 ${
            isAccepting
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : "bg-green-600 hover:bg-green-700 text-white"
          }`}
        >
          {isAccepting ? (
            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
          ) : (
            <CheckIcon className="w-3 h-3" />
          )}
        </Button>

        <Button
          onClick={handleReject}
          disabled={isAccepting || isRejecting}
          size="sm"
          className={`px-2 py-1 rounded-lg font-medium transition-all duration-300 text-xs h-7 flex items-center gap-1 ${
            isRejecting
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : "bg-red-600 hover:bg-red-700 text-white"
          }`}
        >
          {isRejecting ? (
            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
          ) : (
            <XIcon className="w-3 h-3" />
          )}
        </Button>
      </div>
    </div>
  );
};
